import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '../api/client'
import { getMenuRoundApi } from '../api/kb/menuRound'
import type {
  MenuAction,
  MenuAlert,
  MenuNotes,
  MenuRound,
  MenuRoundState,
  OverrideMap,
  OverrideValue,
  RoundDish,
} from '../api/kb/menuRound'
import { carryOver, periodProblem, roundOverrideMap, toRoundDish } from '../lib/menuRound'
import { kbErrorText, useKnowledge } from './knowledge'

type DishOverride = Record<string, OverrideValue | boolean>

/** A new round being checked against the dishes on sale in POS (nothing is saved until approve / send). */
export interface MenuReview {
  from: string
  to: string
  overrides: OverrideMap
  notes: MenuNotes
  poster_media_id: string | null
}

/**
 * "เมนูรอบนี้" on the knowledge page: one state object from `/api/v1/ai/menu-round`, replaced after every change
 * (like useKnowledge). The review draft lives here too, so switching sections does not lose it. Toasts go through
 * the knowledge page's toast. `alert` feeds the owner's rail dot + banner (GET /alert once, then every state).
 */
export const useMenuRound = defineStore('menuRound', () => {
  const data = ref<MenuRoundState | null>(null)
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  const busy = ref<Set<string>>(new Set())
  const alert = ref<MenuAlert | null>(null)
  const review = ref<MenuReview | null>(null)
  let alertAsked = false

  const me = computed(() => data.value?.me ?? null)
  const canEdit = computed(() => me.value?.can_edit ?? false)
  const canApprove = computed(() => me.value?.can_approve ?? false)
  /** A head's new round or edit waits for the owner (nav badge). */
  const waiting = computed(() => (data.value ? (data.value.proposal ? 1 : 0) + (data.value.current?.pending ? 1 : 0) : 0))
  const badge = computed(() => (waiting.value > 0 ? 1 : 0))

  /** The review list: dishes on sale now with the draft's corrections applied. */
  const reviewDishes = computed<RoundDish[]>(() => {
    const d = data.value
    const r = review.value
    if (!d || !r) return []
    return d.live.dishes.map((x) => toRoundDish(x, r.overrides[x.id]))
  })
  const reviewProblem = computed<string | null>(() => {
    const d = data.value
    const r = review.value
    if (!d || !r) return null
    if (!d.live.ok) return 'ต่อ POS ไม่ได้'
    if (!d.live.dishes.length) return 'ไม่พบเมนูที่ขายอยู่ใน POS'
    if (r.notes.text.length > d.limits.notes_chars) return `หมายเหตุถึง AI ยาวเกิน ${d.limits.notes_chars} ตัวอักษร`
    return periodProblem(r.from, r.to, d.limits.max_days)
  })

  function flash(msg: string): void {
    useKnowledge().flash(msg)
  }

  function isBusy(key: string): boolean {
    return busy.value.has(key)
  }

  async function withBusy<T>(key: string, fn: () => Promise<T>): Promise<T | null> {
    if (busy.value.has(key)) return null
    busy.value = new Set(busy.value).add(key)
    try {
      return await fn()
    } catch (e) {
      flash(kbErrorText(e))
      return null
    } finally {
      const next = new Set(busy.value)
      next.delete(key)
      busy.value = next
    }
  }

  /** Every state taken (a load or an action's answer): a load still on its way never overwrites a newer one. */
  let stateSeq = 0
  let lastLoad = 0

  function take(s: MenuRoundState): void {
    stateSeq++
    data.value = s
    alert.value = s.alert
    alertAsked = true
  }

  /** fresh: re-read the POS catalog now (review) — normal page loads use the server's short cache. */
  async function load(opts: { fresh?: boolean } = {}): Promise<void> {
    const n = ++lastLoad
    const at = stateSeq
    loading.value = true
    try {
      const s = await getMenuRoundApi().load(opts.fresh ? { fresh: true } : undefined)
      if (n !== lastLoad || at !== stateSeq) return // a newer load, or an approve / edit answered meanwhile
      take(s)
      loadError.value = null
    } catch (e) {
      if (n === lastLoad) loadError.value = kbErrorText(e)
    } finally {
      if (n === lastLoad) loading.value = false
    }
  }

  /** Rail dot for the owner: one light call, never blocks, errors ignored. */
  async function ensureAlert(): Promise<void> {
    if (alertAsked) return
    alertAsked = true
    try {
      alert.value = await getMenuRoundApi().alert()
    } catch {
      alertAsked = false
    }
  }

  // ------------------------------------------------ review

  /** Open the review from the dishes on sale now, carrying the current round's corrections / notes / poster. */
  async function openReview(): Promise<void> {
    await load({ fresh: true })
    const d = data.value
    if (!d) return
    const carry = carryOver(d.current ?? d.proposal, d.live.dishes)
    review.value = { from: d.default_period.from, to: d.default_period.to, ...carry }
  }

  function cancelReview(): void {
    review.value = null
  }

  function setReviewDish(id: string, ov: DishOverride | null): void {
    const r = review.value
    if (!r) return
    const next = { ...r.overrides }
    if (ov && Object.keys(ov).length) next[id] = ov
    else delete next[id]
    r.overrides = next
  }

  function toggleNote(kind: 'new_ids' | 'recommended_ids', id: string): void {
    const r = review.value
    if (!r) return
    const list = r.notes[kind]
    r.notes = { ...r.notes, [kind]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] }
  }

  async function submitReview(): Promise<boolean> {
    const d = data.value
    const r = review.value
    if (!d || !r || reviewProblem.value || busy.value.has('review')) return false
    const ids = d.live.dishes.map((x) => x.id)
    const onSale = new Set(ids)
    const overrides: OverrideMap = {}
    for (const [id, o] of Object.entries(r.overrides)) if (onSale.has(id) && Object.keys(o).length) overrides[id] = o
    const owner = canApprove.value
    busy.value = new Set(busy.value).add('review')
    try {
      const s = await getMenuRoundApi().create({
        valid_from: r.from,
        valid_to: r.to,
        overrides,
        notes: {
          new_ids: r.notes.new_ids.filter((x) => onSale.has(x)),
          recommended_ids: r.notes.recommended_ids.filter((x) => onSale.has(x)),
          text: r.notes.text.trim(),
        },
        poster_media_id: r.poster_media_id,
        expect_ids: ids,
      })
      take(s)
      review.value = null
      flash(owner ? 'อนุมัติเมนูรอบนี้แล้ว AI เริ่มใช้ภายในไม่กี่วินาที' : 'ส่งให้เจ้าของอนุมัติแล้ว AI ยังไม่ใช้จนกว่าจะอนุมัติ')
      return true
    } catch (e) {
      flash(kbErrorText(e))
      // POS changed while checking: show the fresh list (the corrections for dishes still on sale stay)
      if (e instanceof ApiError && e.status === 409) await load({ fresh: true })
      return false
    } finally {
      const next = new Set(busy.value)
      next.delete('review')
      busy.value = next
    }
  }

  // ------------------------------------------------ a live round / proposal

  /** The map a dish edit starts from: a head's own waiting edit first (edits pile up), else the round's. */
  function baseMap(round: MenuRound): OverrideMap {
    if (!canApprove.value && round.status === 'approved' && round.pending?.overrides) return round.pending.overrides
    return roundOverrideMap(round.dishes)
  }

  function editBase(round: MenuRound, dishId: string): DishOverride | null {
    return baseMap(round)[dishId] ?? null
  }

  async function saveDish(round: MenuRound, dishId: string, ov: DishOverride | null): Promise<boolean> {
    const next = { ...baseMap(round) }
    if (ov && Object.keys(ov).length) next[dishId] = ov
    else delete next[dishId]
    const owner = canApprove.value
    const s = await withBusy(`round:${round.id}`, () => getMenuRoundApi().edit(round.id, { overrides: next }))
    if (!s) return false
    take(s)
    flash(
      owner
        ? 'บันทึกแล้ว AI ใช้ค่าใหม่ภายในไม่กี่วินาที'
        : round.status === 'approved'
          ? 'ส่งให้เจ้าของอนุมัติแล้ว AI ยังใช้ฉบับเดิมจนกว่าจะอนุมัติ'
          : 'บันทึกข้อเสนอแล้ว',
    )
    return true
  }

  async function act(round: MenuRound, action: MenuAction): Promise<boolean> {
    const owner = canApprove.value
    const isProposal = round.status === 'pending_approval'
    const s = await withBusy(`round:${round.id}`, () => getMenuRoundApi().act(round.id, action))
    if (!s) return false
    take(s)
    const msg =
      action === 'approve'
        ? isProposal
          ? 'อนุมัติเมนูรอบนี้แล้ว AI เริ่มใช้ภายในไม่กี่วินาที'
          : 'อนุมัติการแก้แล้ว AI ใช้ค่าใหม่ภายในไม่กี่วินาที'
        : action === 'reject'
          ? owner
            ? isProposal
              ? 'ไม่อนุมัติแล้ว AI ไม่ใช้รอบที่เสนอนี้'
              : 'ไม่อนุมัติการแก้แล้ว AI ใช้ฉบับเดิมต่อ'
            : 'ยกเลิกข้อเสนอแล้ว'
          : 'ปิดใช้แล้ว AI จะไม่บอกรายละเอียดเมนูจนกว่าจะอนุมัติรอบใหม่'
    flash(msg)
    return true
  }

  return {
    data, loading, loadError, alert, review, me, canEdit, canApprove, waiting, badge, reviewDishes, reviewProblem,
    isBusy, load, ensureAlert, openReview, cancelReview, setReviewDish, toggleNote, submitReview, editBase, saveDish, act,
  }
})
